import React, { useEffect, useMemo, useState } from "react";
import { compactPageItems } from "../../components/ui/compactPagination";
import {
  Table,
  Button,
  Form,
  Modal,
  InputGroup,
  Pagination,
  Badge,
  Spinner,
} from "react-bootstrap";
import api from "../../api";
import Can from "../../components/Can";
import ConfirmModal from "../../components/common/ConfirmModal";
import { usePermissions } from "../../context/PermissionContext";
import { toast } from "react-toastify";
import "./ProjectTypeManagementPage.css";

// src/pages/admins/ProjectTypeManagementPage.jsx
//
// Admin-only CRUD for Project Types -- mirrors TaskTypeManagementPage.jsx's
// list/search/create/edit/activate-deactivate/delete pattern (same
// unit-page/unit-card/unit-table/unit-btn classes, already loaded globally
// via AdminUnitPage.css -- no new stylesheet needed for a page this simple).
// The `project_types` module is strictly Admin-only per the locked API
// contract (no per-project owner/manager bypass like project_milestones
// has), so every mutation button here is a hard <Can> gate, never shown
// unconditionally.
//
// Each row also opens a nested "Process Template" editor (ProcessTemplateModal
// below) for that type's global phase/milestone template -- the structure a
// new project copies into its own editable process via
// POST /projects/:id/process/initialize (see ProjectWorkspaceProcessTab.jsx).

const initialForm = {
  name: "",
  code: "",
  description: "",
  display_order: "",
};

// ---------------------------------------------------------------------
// Process Template editor -- nested per Project Type. Two independent
// lists (Phases, Milestones), each with an inline "add" row and inline
// edit-in-place for existing rows (no second-level modal, to keep this
// already-nested UI from getting three modals deep). Reordering uses
// simple up/down buttons: phases call the documented
// PUT .../phases/reorder {order:[...]} endpoint; milestones have no such
// endpoint in the locked contract, so up/down there swaps the `sequence`
// value of the two adjacent rows via two PUT calls instead.
// ---------------------------------------------------------------------
const emptyPhaseForm = { name: "", description: "", default_duration_days: "" };
const emptyMilestoneForm = { name: "", description: "", process_phase_id: "", relative_day_offset: "" };

function ProcessTemplateModal({ show, projectType, onClose }) {
  const [phases, setPhases] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(false);

  const [newPhase, setNewPhase] = useState(emptyPhaseForm);
  const [savingPhase, setSavingPhase] = useState(false);
  const [editPhaseId, setEditPhaseId] = useState(null);
  const [editPhaseForm, setEditPhaseForm] = useState(emptyPhaseForm);
  const [deletePhaseTarget, setDeletePhaseTarget] = useState(null);

  const [newMilestone, setNewMilestone] = useState(emptyMilestoneForm);
  const [savingMilestone, setSavingMilestone] = useState(false);
  const [editMilestoneId, setEditMilestoneId] = useState(null);
  const [editMilestoneForm, setEditMilestoneForm] = useState(emptyMilestoneForm);
  const [deleteMilestoneTarget, setDeleteMilestoneTarget] = useState(null);

  const projectTypeId = projectType?.id;

  const load = () => {
    if (!projectTypeId) return;
    setLoading(true);
    Promise.all([
      api.get(`/project-types/${projectTypeId}/process/phases`),
      api.get(`/project-types/${projectTypeId}/process/milestones`),
    ])
      .then(([phaseRes, milestoneRes]) => {
        const p = Array.isArray(phaseRes.data?.data) ? phaseRes.data.data : [];
        const m = Array.isArray(milestoneRes.data?.data) ? milestoneRes.data.data : [];
        setPhases([...p].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0)));
        setMilestones([...m].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0)));
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || "Failed to load process template");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (show) load();
    else {
      setNewPhase(emptyPhaseForm);
      setNewMilestone(emptyMilestoneForm);
      setEditPhaseId(null);
      setEditMilestoneId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, projectTypeId]);

  const handleAddPhase = async () => {
    if (!newPhase.name.trim()) {
      toast.warning("Phase name is required");
      return;
    }
    try {
      setSavingPhase(true);
      await api.post(`/project-types/${projectTypeId}/process/phases`, {
        name: newPhase.name.trim(),
        description: newPhase.description.trim() || null,
        default_duration_days: newPhase.default_duration_days === "" ? null : Number(newPhase.default_duration_days),
      });
      toast.success("Phase added");
      setNewPhase(emptyPhaseForm);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to add phase");
    } finally {
      setSavingPhase(false);
    }
  };

  const openEditPhase = (phase) => {
    setEditPhaseId(phase.id);
    setEditPhaseForm({
      name: phase.name || "",
      description: phase.description || "",
      default_duration_days: phase.default_duration_days ?? "",
    });
  };

  const saveEditPhase = async () => {
    if (!editPhaseForm.name.trim()) {
      toast.warning("Phase name is required");
      return;
    }
    try {
      setSavingPhase(true);
      await api.put(`/project-types/${projectTypeId}/process/phases/${editPhaseId}`, {
        name: editPhaseForm.name.trim(),
        description: editPhaseForm.description.trim() || null,
        default_duration_days: editPhaseForm.default_duration_days === "" ? null : Number(editPhaseForm.default_duration_days),
      });
      toast.success("Phase updated");
      setEditPhaseId(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update phase");
    } finally {
      setSavingPhase(false);
    }
  };

  const confirmDeletePhase = async () => {
    if (!deletePhaseTarget) return;
    try {
      await api.delete(`/project-types/${projectTypeId}/process/phases/${deletePhaseTarget.id}`);
      toast.success("Phase deleted");
      setDeletePhaseTarget(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete phase");
      setDeletePhaseTarget(null);
    }
  };

  const movePhase = async (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= phases.length) return;
    const reordered = [...phases];
    const [moved] = reordered.splice(index, 1);
    reordered.splice(targetIndex, 0, moved);
    setPhases(reordered);
    try {
      await api.put(`/project-types/${projectTypeId}/process/phases/reorder`, {
        order: reordered.map((p) => p.id),
      });
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to reorder phases");
      load();
    }
  };

  const handleAddMilestone = async () => {
    if (!newMilestone.name.trim()) {
      toast.warning("Milestone name is required");
      return;
    }
    try {
      setSavingMilestone(true);
      await api.post(`/project-types/${projectTypeId}/process/milestones`, {
        name: newMilestone.name.trim(),
        description: newMilestone.description.trim() || null,
        process_phase_id: newMilestone.process_phase_id || null,
        relative_day_offset: newMilestone.relative_day_offset === "" ? null : Number(newMilestone.relative_day_offset),
      });
      toast.success("Milestone added");
      setNewMilestone(emptyMilestoneForm);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to add milestone");
    } finally {
      setSavingMilestone(false);
    }
  };

  const openEditMilestone = (m) => {
    setEditMilestoneId(m.id);
    setEditMilestoneForm({
      name: m.name || "",
      description: m.description || "",
      process_phase_id: m.process_phase_id ? String(m.process_phase_id) : "",
      relative_day_offset: m.relative_day_offset ?? "",
    });
  };

  const saveEditMilestone = async () => {
    if (!editMilestoneForm.name.trim()) {
      toast.warning("Milestone name is required");
      return;
    }
    try {
      setSavingMilestone(true);
      await api.put(`/project-types/${projectTypeId}/process/milestones/${editMilestoneId}`, {
        name: editMilestoneForm.name.trim(),
        description: editMilestoneForm.description.trim() || null,
        process_phase_id: editMilestoneForm.process_phase_id || null,
        relative_day_offset: editMilestoneForm.relative_day_offset === "" ? null : Number(editMilestoneForm.relative_day_offset),
      });
      toast.success("Milestone updated");
      setEditMilestoneId(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update milestone");
    } finally {
      setSavingMilestone(false);
    }
  };

  const confirmDeleteMilestone = async () => {
    if (!deleteMilestoneTarget) return;
    try {
      await api.delete(`/project-types/${projectTypeId}/process/milestones/${deleteMilestoneTarget.id}`);
      toast.success("Milestone deleted");
      setDeleteMilestoneTarget(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete milestone");
      setDeleteMilestoneTarget(null);
    }
  };

  // No documented reorder endpoint for template milestones -- swap the
  // `sequence` value of the two adjacent rows directly instead.
  const moveMilestone = async (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= milestones.length) return;
    const a = milestones[index];
    const b = milestones[targetIndex];
    try {
      await Promise.all([
        api.put(`/project-types/${projectTypeId}/process/milestones/${a.id}`, {
          name: a.name,
          description: a.description ?? null,
          process_phase_id: a.process_phase_id ?? null,
          relative_day_offset: a.relative_day_offset ?? null,
          sequence: b.sequence,
        }),
        api.put(`/project-types/${projectTypeId}/process/milestones/${b.id}`, {
          name: b.name,
          description: b.description ?? null,
          process_phase_id: b.process_phase_id ?? null,
          relative_day_offset: b.relative_day_offset ?? null,
          sequence: a.sequence,
        }),
      ]);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to reorder milestones");
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Process Template — {projectType?.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small">
          This is the reusable template copied into a project's own editable process when someone runs
          "Configure Process" for a project of this type (see the Process tab). Editing this template never
          changes any already-configured project.
        </p>

        {loading ? (
          <div className="unit-loader">
            <Spinner animation="border" />
            <p>Loading template…</p>
          </div>
        ) : (
          <>
            <h6 className="fw-bold mt-2">Phases</h6>
            <Table responsive size="sm" className="unit-table">
              <thead>
                <tr>
                  <th style={{ width: 60 }}>Seq</th>
                  <th>Name</th>
                  <th>Description</th>
                  <th style={{ width: 110 }}>Duration (days)</th>
                  <th className="text-end" style={{ width: 140 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {phases.map((phase, index) =>
                  editPhaseId === phase.id ? (
                    <tr key={phase.id}>
                      <td>{phase.sequence}</td>
                      <td>
                        <Form.Control
                          size="sm"
                          value={editPhaseForm.name}
                          onChange={(e) => setEditPhaseForm((f) => ({ ...f, name: e.target.value }))}
                        />
                      </td>
                      <td>
                        <Form.Control
                          size="sm"
                          value={editPhaseForm.description}
                          onChange={(e) => setEditPhaseForm((f) => ({ ...f, description: e.target.value }))}
                        />
                      </td>
                      <td>
                        <Form.Control
                          size="sm"
                          type="number"
                          value={editPhaseForm.default_duration_days}
                          onChange={(e) => setEditPhaseForm((f) => ({ ...f, default_duration_days: e.target.value }))}
                        />
                      </td>
                      <td className="text-end">
                        <Button size="sm" className="unit-btn unit-btn-primary me-1" onClick={saveEditPhase} disabled={savingPhase}>
                          Save
                        </Button>
                        <Button size="sm" variant="outline-secondary" onClick={() => setEditPhaseId(null)} disabled={savingPhase}>
                          Cancel
                        </Button>
                      </td>
                    </tr>
                  ) : (
                    <tr key={phase.id}>
                      <td>{phase.sequence}</td>
                      <td><strong>{phase.name}</strong></td>
                      <td className="text-muted small">{phase.description || "—"}</td>
                      <td>{phase.default_duration_days ?? "—"}</td>
                      <td className="text-end">
                        <div className="unit-actions justify-content-end">
                          <Button className="unit-icon-btn" title="Move up" disabled={index === 0} onClick={() => movePhase(index, -1)}>
                            <i className="fa-solid fa-arrow-up" aria-hidden="true"></i>
                          </Button>
                          <Button className="unit-icon-btn" title="Move down" disabled={index === phases.length - 1} onClick={() => movePhase(index, 1)}>
                            <i className="fa-solid fa-arrow-down" aria-hidden="true"></i>
                          </Button>
                          <Button className="unit-icon-btn unit-edit" title="Edit" onClick={() => openEditPhase(phase)}>
                            <i className="fa-solid fa-pen" aria-hidden="true"></i>
                          </Button>
                          <Button className="unit-icon-btn unit-delete" title="Delete" onClick={() => setDeletePhaseTarget(phase)}>
                            <i className="fa-solid fa-trash" aria-hidden="true"></i>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )
                )}
                <tr>
                  <td className="text-muted">New</td>
                  <td>
                    <Form.Control
                      size="sm"
                      placeholder="Phase name"
                      value={newPhase.name}
                      onChange={(e) => setNewPhase((f) => ({ ...f, name: e.target.value }))}
                    />
                  </td>
                  <td>
                    <Form.Control
                      size="sm"
                      placeholder="Description (optional)"
                      value={newPhase.description}
                      onChange={(e) => setNewPhase((f) => ({ ...f, description: e.target.value }))}
                    />
                  </td>
                  <td>
                    <Form.Control
                      size="sm"
                      type="number"
                      placeholder="Days"
                      value={newPhase.default_duration_days}
                      onChange={(e) => setNewPhase((f) => ({ ...f, default_duration_days: e.target.value }))}
                    />
                  </td>
                  <td className="text-end">
                    <Button size="sm" className="unit-btn unit-btn-primary" onClick={handleAddPhase} disabled={savingPhase}>
                      <i className="fa-solid fa-plus" aria-hidden="true"></i> Add
                    </Button>
                  </td>
                </tr>
                {phases.length === 0 && (
                  <tr />
                )}
              </tbody>
            </Table>

            <h6 className="fw-bold mt-4">Milestones</h6>
            <Table responsive size="sm" className="unit-table">
              <thead>
                <tr>
                  <th style={{ width: 60 }}>Seq</th>
                  <th>Name</th>
                  <th>Phase</th>
                  <th style={{ width: 120 }}>Day Offset</th>
                  <th className="text-end" style={{ width: 140 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {milestones.map((m, index) =>
                  editMilestoneId === m.id ? (
                    <tr key={m.id}>
                      <td>{m.sequence}</td>
                      <td>
                        <Form.Control
                          size="sm"
                          value={editMilestoneForm.name}
                          onChange={(e) => setEditMilestoneForm((f) => ({ ...f, name: e.target.value }))}
                        />
                      </td>
                      <td>
                        <Form.Select
                          size="sm"
                          value={editMilestoneForm.process_phase_id}
                          onChange={(e) => setEditMilestoneForm((f) => ({ ...f, process_phase_id: e.target.value }))}
                        >
                          <option value="">Unscoped</option>
                          {phases.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </Form.Select>
                      </td>
                      <td>
                        <Form.Control
                          size="sm"
                          type="number"
                          value={editMilestoneForm.relative_day_offset}
                          onChange={(e) => setEditMilestoneForm((f) => ({ ...f, relative_day_offset: e.target.value }))}
                        />
                      </td>
                      <td className="text-end">
                        <Button size="sm" className="unit-btn unit-btn-primary me-1" onClick={saveEditMilestone} disabled={savingMilestone}>
                          Save
                        </Button>
                        <Button size="sm" variant="outline-secondary" onClick={() => setEditMilestoneId(null)} disabled={savingMilestone}>
                          Cancel
                        </Button>
                      </td>
                    </tr>
                  ) : (
                    <tr key={m.id}>
                      <td>{m.sequence}</td>
                      <td><strong>{m.name}</strong></td>
                      <td>{phases.find((p) => p.id === m.process_phase_id)?.name || <span className="text-muted">Unscoped</span>}</td>
                      <td>{m.relative_day_offset ?? "—"}</td>
                      <td className="text-end">
                        <div className="unit-actions justify-content-end">
                          <Button className="unit-icon-btn" title="Move up" disabled={index === 0} onClick={() => moveMilestone(index, -1)}>
                            <i className="fa-solid fa-arrow-up" aria-hidden="true"></i>
                          </Button>
                          <Button className="unit-icon-btn" title="Move down" disabled={index === milestones.length - 1} onClick={() => moveMilestone(index, 1)}>
                            <i className="fa-solid fa-arrow-down" aria-hidden="true"></i>
                          </Button>
                          <Button className="unit-icon-btn unit-edit" title="Edit" onClick={() => openEditMilestone(m)}>
                            <i className="fa-solid fa-pen" aria-hidden="true"></i>
                          </Button>
                          <Button className="unit-icon-btn unit-delete" title="Delete" onClick={() => setDeleteMilestoneTarget(m)}>
                            <i className="fa-solid fa-trash" aria-hidden="true"></i>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )
                )}
                <tr>
                  <td className="text-muted">New</td>
                  <td>
                    <Form.Control
                      size="sm"
                      placeholder="Milestone name"
                      value={newMilestone.name}
                      onChange={(e) => setNewMilestone((f) => ({ ...f, name: e.target.value }))}
                    />
                  </td>
                  <td>
                    <Form.Select
                      size="sm"
                      value={newMilestone.process_phase_id}
                      onChange={(e) => setNewMilestone((f) => ({ ...f, process_phase_id: e.target.value }))}
                    >
                      <option value="">Unscoped</option>
                      {phases.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </Form.Select>
                  </td>
                  <td>
                    <Form.Control
                      size="sm"
                      type="number"
                      placeholder="Days"
                      value={newMilestone.relative_day_offset}
                      onChange={(e) => setNewMilestone((f) => ({ ...f, relative_day_offset: e.target.value }))}
                    />
                  </td>
                  <td className="text-end">
                    <Button size="sm" className="unit-btn unit-btn-primary" onClick={handleAddMilestone} disabled={savingMilestone}>
                      <i className="fa-solid fa-plus" aria-hidden="true"></i> Add
                    </Button>
                  </td>
                </tr>
              </tbody>
            </Table>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button className="unit-btn unit-btn-light" onClick={onClose}>Close</Button>
      </Modal.Footer>

      <ConfirmModal
        show={!!deletePhaseTarget}
        onHide={() => setDeletePhaseTarget(null)}
        onConfirm={confirmDeletePhase}
        title="Delete Phase?"
        message={`Delete template phase "${deletePhaseTarget?.name}"? This only affects the template, not any already-configured project.`}
      />
      <ConfirmModal
        show={!!deleteMilestoneTarget}
        onHide={() => setDeleteMilestoneTarget(null)}
        onConfirm={confirmDeleteMilestone}
        title="Delete Milestone?"
        message={`Delete template milestone "${deleteMilestoneTarget?.name}"? This only affects the template, not any already-configured project.`}
      />
    </Modal>
  );
}

export default function ProjectTypeManagementPage() {
  const { can } = usePermissions();

  const [projectTypes, setProjectTypes] = useState([]);
  const [total, setTotal] = useState(0);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState(initialForm);
  const [editId, setEditId] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [templateTarget, setTemplateTarget] = useState(null);

  useEffect(() => {
    fetchProjectTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter, currentPage, pageSize]);

  const fetchProjectTypes = async () => {
    try {
      setLoading(true);
      const res = await api.get("/project-types", {
        params: {
          search: search || undefined,
          is_active: statusFilter === "" ? undefined : statusFilter,
          page: currentPage,
          pageSize,
        },
      });
      setProjectTypes(Array.isArray(res.data?.data) ? res.data.data : []);
      setTotal(res.data?.meta?.total || 0);
    } catch (err) {
      console.error("Fetch project types error:", err);
      toast.error(err.response?.data?.message || "Failed to fetch project types");
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.max(Math.ceil(total / pageSize), 1);

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const openAdd = () => {
    setForm(initialForm);
    setEditId(null);
    setShowModal(true);
  };

  const openEdit = (pt) => {
    setForm({
      name: pt.name || "",
      code: pt.code || "",
      description: pt.description || "",
      display_order: pt.display_order ?? "",
    });
    setEditId(pt.id);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditId(null);
    setForm(initialForm);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.warning("Project type name is required");
      return;
    }

    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      display_order: form.display_order === "" ? 0 : Number(form.display_order),
    };

    if (!editId) {
      payload.code = form.code.trim() || undefined;
    }

    try {
      setSaving(true);
      if (editId) {
        await api.put(`/project-types/${editId}`, payload);
        toast.success("Project type updated");
      } else {
        await api.post("/project-types", payload);
        toast.success("Project type created");
      }
      closeModal();
      fetchProjectTypes();
    } catch (err) {
      console.error("Save project type error:", err);
      toast.error(err.response?.data?.message || "Failed to save project type");
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (pt) => {
    try {
      await api.patch(`/project-types/${pt.id}/status`, { is_active: !pt.is_active });
      toast.success(pt.is_active ? "Project type deactivated" : "Project type activated");
      fetchProjectTypes();
    } catch (err) {
      console.error("Toggle status error:", err);
      toast.error(err.response?.data?.message || "Failed to update status");
    }
  };

  const openDeleteModal = (pt) => {
    setDeleteTarget(pt);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setDeleteTarget(null);
    setShowDeleteModal(false);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/project-types/${deleteTarget.id}`);
      toast.success("Project type deleted");
      closeDeleteModal();
      fetchProjectTypes();
    } catch (err) {
      console.error("Delete project type error:", err);
      const data = err.response?.data;
      if (data?.code === "PROJECT_TYPE_IN_USE") {
        toast.error(`Cannot delete: ${data.details?.projects || 0} project(s) use this type. Deactivate instead.`);
      } else {
        toast.error(data?.message || "Failed to delete project type");
      }
      closeDeleteModal();
    }
  };

  const canManage = useMemo(
    () => can("project_types", "create") || can("project_types", "edit"),
    [can]
  );

  return (
    <div className="unit-page ptm-page">

      <div className="unit-header ptm-heading-box">
        <div className="ptm-heading-content">
          <div className="ptm-heading-icon">
            <i className="fa-solid fa-diagram-project" aria-hidden="true"></i>
          </div>
          <div>
            <div className="unit-page-label">Configuration</div>
            <h2>Project Type Management</h2>
            <p>Define project types and their reusable phase/milestone process templates. New projects copy a type's template into their own editable process.</p>
          </div>
        </div>

        <div className="unit-header-actions">
          <Button className="unit-btn unit-btn-light" onClick={fetchProjectTypes}>
            <i className="fa-solid fa-rotate" aria-hidden="true"></i>
            Refresh
          </Button>

          <Can moduleKey="project_types" actionKey="create">
            <Button className="unit-btn unit-btn-primary" onClick={openAdd}>
              <i className="fa-solid fa-plus" aria-hidden="true"></i>
              Add Project Type
            </Button>
          </Can>
        </div>
      </div>

      <div className="unit-stats-row">
        <div className="unit-stat-card">
          <div className="unit-stat-text">
            <span>Total Project Types</span>
            <strong>{total}</strong>
          </div>
        </div>
        <div className="unit-stat-card unit-stat-card--secondary">
          <div className="unit-stat-text">
            <span>Current Page</span>
            <strong>{currentPage}</strong>
          </div>
        </div>
      </div>

      <div className="unit-card">
        <div className="unit-toolbar">
          <div>
            <h5>
              <i className="fa-solid fa-diagram-project" aria-hidden="true"></i>
              Project Type List
            </h5>
            <p>Search, filter, add, update, activate/deactivate, delete or manage a type's process template.</p>
          </div>

          <div className="unit-toolbar-actions ptm-toolbar-actions">
            <InputGroup className="unit-search ptm-search">
              <InputGroup.Text aria-hidden="true">
                <i className="fa-solid fa-magnifying-glass"></i>
              </InputGroup.Text>
              <Form.Control
                placeholder="Search name or code..."
                aria-label="Search project type name or code"
                value={search}
                onChange={(e) => {
                  setCurrentPage(1);
                  setSearch(e.target.value);
                }}
              />
            </InputGroup>

            <Form.Select
              className="ptm-filter-select ptm-status-select"
              value={statusFilter}
              onChange={(e) => {
                setCurrentPage(1);
                setStatusFilter(e.target.value);
              }}
            >
              <option value="">All Statuses</option>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </Form.Select>

            <Form.Select
              className="unit-page-size ptm-filter-select ptm-rows-select"
              value={pageSize}
              onChange={(e) => {
                setCurrentPage(1);
                setPageSize(Number(e.target.value));
              }}
            >
              <option value={10}>10 rows</option>
              <option value={20}>20 rows</option>
              <option value={50}>50 rows</option>
            </Form.Select>
          </div>
        </div>

        <div className="unit-table-wrapper">
          {loading ? (
            <div className="unit-loader">
              <Spinner animation="border" />
              <p>Loading project types...</p>
            </div>
          ) : (
            <Table responsive className="unit-table ptm-table">
              <colgroup>
                <col style={{ width: "15%" }} />
                <col style={{ width: "17%" }} />
                <col style={{ width: "35%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "9%" }} />
                <col style={{ width: "14%" }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Code</th>
                  <th>Description</th>
                  <th className="ptm-col-center">Display Order</th>
                  <th>Status</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>

              <tbody>
                {projectTypes.length > 0 ? (
                  projectTypes.map((pt) => (
                    <tr key={pt.id}>
                      <td className="ptm-name-cell"><strong>{pt.name}</strong></td>
                      <td className="ptm-code-cell">{pt.code}</td>
                      <td className="text-muted ptm-desc-cell">{pt.description || "—"}</td>
                      <td className="ptm-col-center">{pt.display_order ?? 0}</td>
                      <td>
                        <Badge className={`unit-status${pt.is_active ? "" : " unit-status-inactive"}`}>
                          {pt.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </td>
                      <td>
                        <div className="unit-actions ptm-actions justify-content-end">
                          {canManage && (
                            <Button
                              className="unit-icon-btn ptm-action-manage"
                              title="Manage Process Template"
                              aria-label={`Manage process template for ${pt.name}`}
                              onClick={() => setTemplateTarget(pt)}
                            >
                              <i className="fa-solid fa-sitemap" aria-hidden="true"></i>
                            </Button>
                          )}

                          <Can moduleKey="project_types" actionKey="edit">
                            <Button
                              className="unit-icon-btn unit-edit ptm-action-edit"
                              title="Edit Project Type"
                              aria-label={`Edit ${pt.name || "project type"}`}
                              onClick={() => openEdit(pt)}
                            >
                              <i className="fa-solid fa-pen" aria-hidden="true"></i>
                            </Button>
                          </Can>

                          {(can("project_types", "activate") || can("project_types", "deactivate")) && (
                            <Button
                              className={`unit-icon-btn ptm-action-toggle${pt.is_active ? " ptm-action-toggle--deactivate" : " ptm-action-toggle--activate"}`}
                              title={pt.is_active ? "Deactivate Project Type" : "Activate Project Type"}
                              aria-label={`${pt.is_active ? "Deactivate" : "Activate"} ${pt.name || "project type"}`}
                              onClick={() => toggleStatus(pt)}
                            >
                              <i className={`fa-solid ${pt.is_active ? "fa-pause" : "fa-play"}`} aria-hidden="true"></i>
                            </Button>
                          )}

                          <Can moduleKey="project_types" actionKey="delete">
                            <Button
                              className="unit-icon-btn unit-delete ptm-action-delete"
                              title="Delete Project Type"
                              aria-label={`Delete ${pt.name || "project type"}`}
                              onClick={() => openDeleteModal(pt)}
                            >
                              <i className="fa-solid fa-trash" aria-hidden="true"></i>
                            </Button>
                          </Can>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6">
                      <div className="unit-empty">
                        <div className="unit-empty-icon">
                          <i className="fa-solid fa-diagram-project" aria-hidden="true"></i>
                        </div>
                        <h5>No project types found</h5>
                        <p>Try another search or add a new project type.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </div>

        <div className="unit-footer">
          <div className="unit-count-text">
            Showing <strong>{projectTypes.length}</strong> of <strong>{total}</strong> project types
          </div>

          {totalPages > 1 && (
            <Pagination className="unit-pagination">
              <Pagination.Prev disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)} />
              {compactPageItems([...Array(totalPages)].map((_, i) => (
                <Pagination.Item key={i + 1} active={i + 1 === currentPage} onClick={() => setCurrentPage(i + 1)}>
                  {i + 1}
                </Pagination.Item>
              )), currentPage)}
              <Pagination.Next disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => p + 1)} />
            </Pagination>
          )}
        </div>
      </div>

      <Modal show={showModal} onHide={closeModal} centered className="unit-modal" size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editId ? "Edit Project Type" : "Add New Project Type"}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="row g-3">
            <div className="col-md-6">
              <Form.Label>Name</Form.Label>
              <Form.Control
                placeholder="e.g. Software Implementation"
                value={form.name}
                onChange={(e) => handleChange("name", e.target.value)}
              />
            </div>

            <div className="col-md-6">
              <Form.Label>Code {editId ? "(immutable)" : "(optional — derived from name if blank)"}</Form.Label>
              <Form.Control
                placeholder="e.g. SOFTWARE_IMPLEMENTATION"
                value={form.code}
                disabled={!!editId}
                onChange={(e) => handleChange("code", e.target.value)}
              />
            </div>

            <div className="col-md-12">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={form.description}
                onChange={(e) => handleChange("description", e.target.value)}
              />
            </div>

            <div className="col-md-4">
              <Form.Label>Display Order</Form.Label>
              <Form.Control
                type="number"
                value={form.display_order}
                onChange={(e) => handleChange("display_order", e.target.value)}
              />
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button className="unit-btn unit-btn-light" onClick={closeModal}>Cancel</Button>
          <Button className="unit-btn unit-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : editId ? "Update Project Type" : "Create Project Type"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDeleteModal} onHide={closeDeleteModal} centered className="unit-modal">
        <Modal.Header closeButton>
          <Modal.Title>Delete Project Type</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="unit-delete-text">
            Are you sure you want to delete "{deleteTarget?.name}"? This action cannot be undone.
            If projects reference it, deletion will be blocked — deactivate it instead.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button className="unit-btn unit-btn-light" onClick={closeDeleteModal}>Cancel</Button>
          <Button className="unit-btn unit-btn-danger" onClick={confirmDelete}>Delete</Button>
        </Modal.Footer>
      </Modal>

      <ProcessTemplateModal
        show={!!templateTarget}
        projectType={templateTarget}
        onClose={() => setTemplateTarget(null)}
      />
    </div>
  );
}
